/**
 * dashboardPage/styled.ts
 * Qué hace: styled-components y keyframes de DashboardPage (orbe animado de Novah,
 *   rail de workspaces, cards, tip diario). CSS puro, sin lógica.
 * Qué exporta: todos los styled (DashboardLayout, NovahCenter, NovahAvatarWrapper,
 *   Orb*, Blob*, Novah*, Workspace*, Card*, NewWorkspaceCard, BigPlus, etc.).
 * Quién lo usa: pages/DashboardPage.tsx.
 */
import styled, { keyframes } from 'styled-components';

/* ── Animations ── */
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const popIn = keyframes`
  0%   { transform: scale(0); opacity: 0; }
  60%  { transform: scale(1.1); }
  100% { transform: scale(1); opacity: 1; }
`;

/* ── Layout ── */
export const DashboardLayout = styled.div`
  /* 1680 y no 1280: con el sidebar de 250px, un monitor de 1920 dejaba ~320px
     muertos a cada lado y las cards entraban de a 6 por fila pudiendo entrar 8. */
  max-width: 1680px;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

/* ── Header — identidad + buscador en UNA franja alta (~15% del alto útil).
    Antes esto era una columna centrada (orbe 200px + h1 + párrafo) que empujaba
    la primera card a ~400px del techo: en un 1080 se veía fila y media. ── */
export const DashboardHeader = styled.header`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.lg};
  padding: 18px 0 16px;
  flex-wrap: wrap;
  animation: ${fadeInUp} 0.45s ease-out both;
`;

export const HeaderIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
  flex: 1 1 300px;
`;

export const HeaderText = styled.div`
  min-width: 0;
`;

/* El orbe está construido con piezas de tamaño fijo (200/170/128px). Escalarlo
   entero es más seguro que re-parametrizar cada capa — y el scale va en un
   wrapper SIN animación: NovahAvatarWrapper tiene un popIn con fill forwards, y
   una animación le gana a una declaración normal en la cascada (el transform
   del wrapper animado se perdería). */
export const OrbScaler = styled.div`
  width: 76px;
  height: 76px;
  flex-shrink: 0;
  /* El hijo mide 200px y desborda: si el contenedor intentara centrarlo, el
     navegador aplica alineación "safe" y lo manda al inicio, con lo cual el
     scale (que va desde el centro) dibujaba el orbe 62px abajo y a la derecha.
     Con origen en la esquina, 200 × 0.38 = 76 cae exacto. */
  position: relative;
  overflow: hidden;
`;

export const OrbScaleInner = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 200px;
  height: 200px;
  transform: scale(0.38);
  transform-origin: top left;

  /* El margen inferior del orbe grande no aplica en la franja compacta. */
  & > div {
    margin-bottom: 0;
  }
`;

/* ── Novah Center Area — ahora solo contiene la grilla y el tip; el saludo se
    mudó al header. Sin centrado vertical: el contenido arranca arriba. ── */
export const NovahCenter = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 0 0 ${({ theme }) => theme.spacing.xl};
  animation: ${fadeInUp} 0.5s ease-out;

  /* El contenido por encima de la capa de partículas (canvas z-index:0). */
  & > *:not(canvas) {
    position: relative;
    z-index: 1;
  }
`;

/* ── Orb animation keyframes ── */
const orbBlobA = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  25% { transform: translate(15px, -20px) scale(1.15); }
  50% { transform: translate(-10px, 15px) scale(0.95); }
  75% { transform: translate(20px, 10px) scale(1.1); }
`;

const orbBlobB = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1.1); }
  25% { transform: translate(-20px, 10px) scale(0.9); }
  50% { transform: translate(15px, -15px) scale(1.2); }
  75% { transform: translate(-10px, -20px) scale(1); }
`;

const orbBlobC = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(25px, 15px) scale(1.15); }
  66% { transform: translate(-15px, -10px) scale(0.9); }
`;

const orbGlowPulse = keyframes`
  0%, 100% { opacity: 0.6; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.05); }
`;

const orbSheenMove = keyframes`
  0%, 100% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

export const NovahAvatarWrapper = styled.div`
  position: relative;
  width: 200px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  animation: ${popIn} 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
`;

/* Outer glow — very subtle ambient haze */
export const OrbAmbient = styled.div`
  position: absolute;
  width: 220px;
  height: 220px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(180, 120, 255, 0.12) 0%,
    rgba(100, 180, 255, 0.06) 50%,
    transparent 70%
  );
  animation: ${orbGlowPulse} 4s ease-in-out infinite;
  z-index: 0;
`;

/* Main orb container — thin ring around avatar */
export const OrbSphere = styled.div`
  position: absolute;
  width: 170px;
  height: 170px;
  border-radius: 50%;
  overflow: hidden;
  z-index: 1;
  background: transparent;
  box-shadow:
    0 0 30px rgba(180, 100, 255, 0.2),
    0 0 60px rgba(140, 80, 230, 0.08);
`;

/* Animated blob layers — subtle color wisps */
const OrbBlob = styled.div<{ $color: string; $size: number; $top: string; $left: string }>`
  position: absolute;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  filter: blur(20px);
  top: ${({ $top }) => $top};
  left: ${({ $left }) => $left};
`;

export const BlobA = styled(OrbBlob)`
  animation: ${orbBlobA} 8s ease-in-out infinite;
`;

export const BlobB = styled(OrbBlob)`
  animation: ${orbBlobB} 10s ease-in-out infinite;
`;

export const BlobC = styled(OrbBlob)`
  animation: ${orbBlobC} 7s ease-in-out infinite;
`;

/* Glass reflection — small highlight */
export const OrbSheen = styled.div`
  position: absolute;
  width: 170px;
  height: 170px;
  border-radius: 50%;
  z-index: 3;
  pointer-events: none;
  background: radial-gradient(circle at 30% 25%, rgba(255,255,255,0.25) 0%, transparent 20%);
  animation: ${orbSheenMove} 25s linear infinite;
`;

/* Thin elegant rim */
export const OrbRim = styled.div`
  position: absolute;
  width: 170px;
  height: 170px;
  border-radius: 50%;
  z-index: 4;
  pointer-events: none;
  border: 1px solid rgba(255, 255, 255, 0.12);
`;

export const NovahAvatar = styled.div`
  position: relative;
  width: 128px;
  height: 128px;
  border-radius: 50%;
  overflow: hidden;
  z-index: 5;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  /* Gentle edge blend */
  mask-image: radial-gradient(circle, #000 65%, rgba(0,0,0,0.4) 82%, transparent 100%);
  -webkit-mask-image: radial-gradient(circle, #000 65%, rgba(0,0,0,0.4) 82%, transparent 100%);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const NovahGreeting = styled.h1`
  font-size: ${({ theme }) => theme.fontSize.xxl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 2px 0;
  text-wrap: balance;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.fontSize.xl};
  }
`;

export const NovahSubtext = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
  line-height: 1.5;
  text-wrap: pretty;
  /* Los contadores cambian al filtrar: sin tabular-nums el texto salta. */
  font-variant-numeric: tabular-nums;
`;

/* ── Buscador — con 35 workspaces encontrar uno era scrollear a ojo. ── */
export const SearchWrap = styled.div`
  position: relative;
  flex: 0 1 320px;
  min-width: 220px;
  margin-left: auto;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex: 1 1 100%;
    margin-left: 0;
  }
`;

export const SearchInput = styled.input`
  width: 100%;
  height: 40px;
  padding: 0 38px 0 38px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  font-family: inherit;
  font-size: ${({ theme }) => theme.fontSize.sm};
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }

  /* Chrome dibuja su propia X en los input[type=search]: sin esto quedan DOS
     botones de limpiar, el nativo y el nuestro. */
  &::-webkit-search-cancel-button {
    -webkit-appearance: none;
    appearance: none;
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}26;
  }
`;

export const SearchIconSlot = styled.span`
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  color: ${({ theme }) => theme.colors.textMuted};
  pointer-events: none;
`;

export const SearchClear = styled.button`
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  width: 26px;
  height: 26px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    color: ${({ theme }) => theme.colors.text};
  }

  /* El botón se ve de 26px pero el área de toque llega a 40×40. */
  &::after {
    content: '';
    position: absolute;
    inset: -7px;
  }
`;

/* Estado vacío del filtro — sin esto, buscar algo inexistente dejaba la
   pantalla en blanco y parecía que la app se había roto. */
export const NoResults = styled.div`
  padding: ${({ theme }) => theme.spacing.xxl} 0;
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
`;

export const RecentActivity = styled.div`
  width: 100%;
  max-width: 440px;
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

export const ActivityTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
  text-align: center;
`;

export const TipCard = styled.div`
  background: ${({ theme }) => theme.colors.surfaceHover};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.md};
  text-align: center;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.5;
`;

/* ── Workspaces rail — el elemento principal: tarjetas grandes (logo + nombre
    + #posts) en una fila centrada. El card "+ New workspace" es la única vía de
    crear (también con 0 workspaces). ── */
export const WorkspacesSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

/* Grilla real en vez de flex-wrap centrado: las filas se llenan de izquierda a
   derecha y aprovechan todo el ancho disponible (antes quedaban huecos y la
   última fila flotaba en el medio). */
export const WorkspaceRail = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 16px;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: repeat(auto-fill, minmax(132px, 1fr));
  }
`;

/* Celda contenedora — el botón de borrar no puede anidarse dentro del card
   (botón dentro de botón), por eso son hermanos en la celda.
   La animación de entrada va ACÁ y no en el card: con fill-mode both el final
   del keyframe (transform: none) le ganaría al translateY del :hover y la card
   dejaría de levantarse (mismo gotcha que la landing de Sound Systems). */
export const WorkspaceCardCell = styled.div<{ $i: number }>`
  position: relative;
  width: 100%;
  animation: ${fadeInUp} 0.32s ease-out both;
  /* Escalonado corto y con techo: con 35 cards, 30ms cada una tardaría 1s en
     terminar de aparecer. */
  animation-delay: ${({ $i }) => Math.min($i * 22, 260)}ms;

  &:hover button[data-delete] {
    opacity: 1;
    pointer-events: auto;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const WorkspaceCard = styled.button`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 22px 16px;
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  cursor: pointer;
  font-family: inherit;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
  /* Sombra en dos capas: el contacto corto define el borde, la difusa da la
     profundidad. Una sola sombra plana se lee como un borde gris. */
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04), 0 2px 6px rgba(0, 0, 0, 0.04);

  &:hover {
    transform: translateY(-3px);
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 8px 22px rgba(59, 130, 246, 0.18);
  }

  &:active {
    transform: scale(0.96);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;

/* Contenedor del logo. El círculo con object-fit: cover RECORTABA los logos:
   la mayoría son rectangulares (M2G, Bolsa de Trabajo, Perfumerías) y el crop
   circular se comía los extremos. Ahora es un tile redondeado con padding y el
   logo entra completo con object-fit: contain. */
export const CardLogoTile = styled.div`
  width: 72px;
  height: 72px;
  flex-shrink: 0;
  padding: 9px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  background: ${({ theme }) => theme.colors.surfaceHover};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

export const CardLogo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

export const CardInitial = styled.span<{ $gradient: string }>`
  width: 72px;
  height: 72px;
  /* Mismo radio que CardLogoTile: si el fallback es un círculo y el logo un
     tile redondeado, la grilla se ve desprolija al mezclarse. */
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $gradient }) => $gradient};
  color: #ffffff;
  font-size: 30px;
  font-weight: 700;
  flex-shrink: 0;
`;

export const CardName = styled.span`
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

export const CardPosts = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  font-variant-numeric: tabular-nums;
`;

export const CardDelete = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: none;
  background: #EF4444;
  color: #fff;
  font-size: 15px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease, transform 0.15s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  z-index: 2;

  &:hover { transform: scale(1.12); background: #DC2626; }

  /* Se ve de 22px pero el área clickeable llega a 40×40 (mínimo táctil). */
  &::after {
    content: '';
    position: absolute;
    inset: -9px;
  }

  /* En touch (sin hover) siempre visible para que se pueda borrar. */
  @media (hover: none) {
    opacity: 1;
    pointer-events: auto;
  }
`;

export const NewWorkspaceCard = styled.button`
  width: 100%;
  min-height: 176px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 22px 16px;
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  border: 1px dashed ${({ theme }) => theme.colors.borderHover};
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  cursor: pointer;
  font-family: inherit;
  /* Nunca transition: all — obliga al navegador a vigilar cada propiedad. */
  transition: border-color 0.2s ease, color 0.2s ease, background-color 0.2s ease,
    transform 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.surfaceHover};
    transform: translateY(-3px);
  }

  &:active {
    transform: scale(0.96);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;

export const BigPlus = styled.span`
  font-size: 36px;
  line-height: 1;
  font-weight: 300;
`;
